import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog';
import { ErrorToast } from 'components';
import { Anchor } from 'lucide-react';
import EmployeeService from 'api/employees';
import { resolveCrewDates } from '../../../utils/resolve-crew-dates';

const AssignVesselModal = ({ open, onClose, member, departments, fetchEmployees }) => {
  const [vessel, setVessel] = useState('');
  const [signOnDate, setSignOnDate] = useState('');
  const [signOffDate, setSignOffDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && member) {
      const currentDept = departments?.find(d => d.name === member.vesselName);
      setVessel(currentDept?._id || '');
      const { signOnDate: resolved, signOffDate: resolvedOff } = resolveCrewDates(member);
      setSignOnDate(resolved || '');
      setSignOffDate(resolvedOff || '');
    }
  }, [open, member, departments]);

  if (!member) return null;

  const vesselOptions = (departments || []).map(dept => ({
    value: dept._id,
    label: dept.name,
  }));

  const onSubmit = async () => {
    try {
      setIsSaving(true);
      const clientId = localStorage.getItem('ROOT_ID');
      await EmployeeService.update(member._id, {
        clientId,
        departments: [vessel],
        signOnDate,
        signOffDate,
      });
      toast.success('Vessel assigned successfully');
      fetchEmployees?.();
      onClose(false);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = !vessel || !signOnDate || isSaving;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle className="tw-flex tw-items-center tw-gap-2">
            <Anchor className="tw-h-5 tw-w-5" />
            {member.vesselName ? 'Transfer Vessel' : 'Assign Vessel'}
          </DialogTitle>
          <DialogDescription>
            {member.vesselName ? 'Transfer' : 'Assign'} {member?.firstName} {member?.lastName} to a vessel.
          </DialogDescription>
        </DialogHeader>
        <div className="tw-space-y-4 tw-py-4">
          <div>
            <label className="tw-text-sm tw-font-medium tw-text-foreground tw-block tw-mb-1.5">Vessel</label>
            <select
              value={vessel}
              onChange={(e) => setVessel(e.target.value)}
              className="tw-w-full tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
            >
              <option value="">Select a vessel...</option>
              {vesselOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <div>
              <label className="tw-text-sm tw-font-medium tw-text-foreground tw-block tw-mb-1.5">Sign On Date</label>
              <input
                type="date"
                value={signOnDate}
                onChange={(e) => setSignOnDate(e.target.value)}
                className="tw-w-full tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
              />
            </div>
            <div>
              <label className="tw-text-sm tw-font-medium tw-text-foreground tw-block tw-mb-1.5">Sign Off Date</label>
              <input
                type="date"
                value={signOffDate}
                onChange={(e) => setSignOffDate(e.target.value)}
                min={signOnDate || undefined}
                className="tw-w-full tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => onClose(false)}
            className="tw-h-9 tw-px-4 tw-rounded-md tw-border tw-border-input tw-bg-background tw-text-sm tw-font-medium hover:tw-bg-accent tw-cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={isDisabled}
            className={`tw-h-9 tw-px-4 tw-rounded-md tw-bg-primary tw-text-primary-foreground tw-text-sm tw-font-medium tw-border-0 ${
              isDisabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'hover:tw-bg-primary/90 tw-cursor-pointer'
            }`}
          >
            {isSaving ? 'Assigning...' : (member.vesselName ? 'Transfer' : 'Assign')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignVesselModal;
